import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAccessToken } from "../utils/auth";
import { API_BASE_URL } from "../utils/api";
import { compareSwimmerNamesByLastName } from "../utils/swimmerSort";
import SwimmerTracker from "./SwimmerTracker";

const API = API_BASE_URL;
const WATCH_KEY = "fantasySwimWatchGroup";

const STROKE_ABBR = {
  Freestyle: "FR",
  Backstroke: "BK",
  Breaststroke: "BR",
  Butterfly: "FLY",
  "Individual Medley": "IM",
};

const STROKE_ORDER = [
  "Freestyle",
  "Backstroke",
  "Breaststroke",
  "Butterfly",
  "Individual Medley",
];

const AGE_GROUP_OPTIONS = [
  { value: "all", label: "All Ages" },
  { value: "6u", label: "6 & Under" },
  { value: "7-8", label: "7-8" },
  { value: "9-10", label: "9-10" },
  { value: "11-12", label: "11-12" },
  { value: "13-14", label: "13-14" },
];

const GENDER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "boys", label: "Boys" },
  { value: "girls", label: "Girls" },
];

const DRAFT_TIPS = [
  "Some swim groups have more swimmers than others. Use early picks to grab swimmers from smaller groups.",
  "A swimmer with slower times and rougher form can have higher upside for point growth.",
  "If you coach earlier shifts, prioritize younger swimmers first, then older swimmers.",
  "If you coach later shifts, prioritize older swimmers first, then younger swimmers.",
  "Pick from each category before doubling up in one category.",
  "Try to choose swimmers who regularly attend practice so you can impact progress.",
  "Avoid drafting swimmers from the same family if possible; shared vacations can hurt availability.",
];

const formatTime = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const seconds = Number(value);
  const minutes = Math.floor(seconds / 60);
  const remainder = (seconds % 60).toFixed(2).padStart(5, "0");
  return minutes > 0 ? `${minutes}:${remainder}` : remainder;
};

const renderTimesSummary = (bestTimes = {}) => {
  const parts = STROKE_ORDER.map((stroke) => {
    const time = bestTimes[stroke];
    if (time == null) return null;
    return `${STROKE_ABBR[stroke]} ${formatTime(time)}`;
  }).filter(Boolean);

  return parts.length ? parts.join(" • ") : "No recorded times";
};

const getAgeGroupKey = (ageValue) => {
  const age = Number(ageValue);
  if (!Number.isFinite(age)) return "unknown";
  if (age <= 6) return "6u";
  if (age <= 8) return "7-8";
  if (age <= 10) return "9-10";
  if (age <= 12) return "11-12";
  if (age <= 14) return "13-14";
  return "unknown";
};

const getGenderKey = (genderValue) => {
  const value = String(genderValue || "")
    .trim()
    .toLowerCase();

  if (["m", "male", "boy", "boys"].includes(value)) return "boys";
  if (["f", "female", "girl", "girls"].includes(value)) return "girls";
  return "unknown";
};

const DraftBoard = ({ user, previewData = null }) => {
  const isPreview = Boolean(previewData);
  const [draftState, setDraftState] = useState(null);
  const [allSwimmers, setAllSwimmers] = useState([]);
  const [availableSwimmers, setAvailableSwimmers] = useState([]);
  const [myTeam, setMyTeam] = useState([]);
  const [watchSet, setWatchSet] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [selectedSwimmer, setSelectedSwimmer] = useState(null);
  const [loading, setLoading] = useState(!isPreview);
  const [status, setStatus] = useState(previewData?.status || "");
  const [toastMessage, setToastMessage] = useState("");
  const [isSubmittingPick, setIsSubmittingPick] = useState(false);
  const [pendingPickName, setPendingPickName] = useState(null);

  const fetchDraftState = useCallback(async () => {
    if (isPreview) return;
    const token = getAccessToken();
    if (!token) {
      setStatus("You must be logged in to access the draft.");
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/draft/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDraftState(res.data.draft || null);
      setAllSwimmers(res.data.all_swimmers || []);
      setAvailableSwimmers(res.data.available_swimmers || []);
      setMyTeam(res.data.my_team || []);
      setStatus("");
    } catch (error) {
      setStatus(error?.response?.data?.detail || "Unable to load draft data.");
    } finally {
      setLoading(false);
    }
  }, [isPreview]);

  useEffect(() => {
    if (isPreview) {
      setDraftState(previewData?.draftState || null);
      setAllSwimmers(previewData?.allSwimmers || []);
      setAvailableSwimmers(previewData?.availableSwimmers || []);
      setMyTeam(previewData?.myTeam || []);
      setStatus(previewData?.status || "");
      setLoading(false);
      return;
    }

    fetchDraftState();
  }, [fetchDraftState, isPreview, previewData]);

  useEffect(() => {
    if (isPreview) {
      const watched = Array.isArray(previewData?.watchNames)
        ? previewData.watchNames.filter((value) => typeof value === "string")
        : [];
      setWatchSet(new Set(watched));
      return;
    }

    try {
      const raw = localStorage.getItem(WATCH_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        setWatchSet(
          new Set(parsed.filter((value) => typeof value === "string")),
        );
      }
    } catch {
      setWatchSet(new Set());
    }
  }, [isPreview, previewData]);

  const watchedSwimmers = useMemo(() => {
    const map = new Map();
    allSwimmers.forEach((swimmer) => {
      map.set(swimmer.name, swimmer);
    });

    return Array.from(watchSet)
      .map((name) => map.get(name))
      .filter(Boolean)
      .sort((a, b) => compareSwimmerNamesByLastName(a?.name, b?.name));
  }, [allSwimmers, watchSet]);

  const filteredAvailableSwimmers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return availableSwimmers
      .filter((swimmer) => {
        const name = String(swimmer?.name || "").toLowerCase();
        const matchesSearch =
          !normalizedSearch || name.includes(normalizedSearch);
        const matchesAge =
          ageGroupFilter === "all" ||
          getAgeGroupKey(swimmer?.age) === ageGroupFilter;
        const matchesGender =
          genderFilter === "all" ||
          getGenderKey(swimmer?.gender) === genderFilter;
        return matchesSearch && matchesAge && matchesGender;
      })
      .sort((a, b) => compareSwimmerNamesByLastName(a?.name, b?.name));
  }, [availableSwimmers, searchTerm, ageGroupFilter, genderFilter]);

  const myTeamByAge = useMemo(() => {
    const swimmerLookup = new Map();
    allSwimmers.forEach((swimmer) => {
      swimmerLookup.set(swimmer.name, swimmer);
    });

    const grouped = new Map();

    [...myTeam]
      .sort((a, b) => compareSwimmerNamesByLastName(a, b))
      .forEach((name) => {
        const swimmer = swimmerLookup.get(name) || {};
        const ageValue = Number(swimmer?.age);
        const hasAge = Number.isFinite(ageValue);
        const groupKey = hasAge ? String(ageValue) : "unknown";

        if (!grouped.has(groupKey)) {
          grouped.set(groupKey, {
            key: groupKey,
            ageSort: hasAge ? ageValue : Number.POSITIVE_INFINITY,
            title: hasAge ? `Age ${ageValue}` : "Unknown Age",
            swimmers: [],
          });
        }

        const genderKey = getGenderKey(swimmer?.gender);
        const genderLabel =
          genderKey === "boys"
            ? "Boy"
            : genderKey === "girls"
              ? "Girl"
              : "Unknown";

        grouped.get(groupKey).swimmers.push({
          name,
          genderKey,
          genderLabel,
          swimmer,
        });
      });

    return Array.from(grouped.values())
      .sort((a, b) => {
        if (a.ageSort !== b.ageSort) return a.ageSort - b.ageSort;
        return a.title.localeCompare(b.title);
      })
      .map((group) => ({
        ...group,
        swimmers: [...group.swimmers].sort((a, b) =>
          compareSwimmerNamesByLastName(a.name, b.name),
        ),
      }));
  }, [myTeam, allSwimmers]);

  const myTurn = Boolean(
    draftState?.active &&
    !draftState?.completed &&
    draftState?.turn?.current_coach === user?.coachName,
  );

  const nextCoachName = useMemo(() => {
    if (!draftState?.active || draftState?.completed) return null;

    const roundOrder = Array.isArray(draftState?.turn?.order_for_round)
      ? draftState.turn.order_for_round
      : [];
    const pickInRound = Number(draftState?.turn?.pick_in_round);

    if (roundOrder.length && Number.isFinite(pickInRound)) {
      const currentIndex = pickInRound - 1;
      if (currentIndex >= 0 && currentIndex < roundOrder.length - 1) {
        return roundOrder[currentIndex + 1] || null;
      }

      const coaches = Array.isArray(draftState?.coaches)
        ? draftState.coaches
        : [];
      if (!coaches.length) return null;
      const nextRound = Number(draftState?.turn?.current_round || 0) + 1;
      const nextOrder = nextRound % 2 === 1 ? coaches : [...coaches].reverse();
      return nextOrder[0] || null;
    }

    return null;
  }, [draftState]);

  const isNextCoach =
    Boolean(nextCoachName) &&
    (user?.coachName || "").trim() === String(nextCoachName).trim();
  const pollIntervalMs = isNextCoach ? 2000 : 10000;

  useEffect(() => {
    if (isPreview) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetchDraftState();
    }, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchDraftState, pollIntervalMs, isPreview]);

  useEffect(() => {
    if (!toastMessage) return;
    const timeoutId = window.setTimeout(() => {
      setToastMessage("");
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [toastMessage]);

  const computeNextDraftTurn = (currentDraftState) => {
    if (!currentDraftState?.active || currentDraftState?.completed) {
      return currentDraftState;
    }

    const turn = currentDraftState.turn || {};
    const coaches = Array.isArray(currentDraftState.coaches)
      ? currentDraftState.coaches
      : [];
    if (coaches.length === 0) {
      return currentDraftState;
    }

    const currentRound = Number(turn.current_round || 1);
    const currentPickInRound = Number(turn.pick_in_round || 1);
    const totalCoaches = coaches.length;
    const currentOrder =
      Array.isArray(turn.order_for_round) &&
      turn.order_for_round.length === totalCoaches
        ? turn.order_for_round
        : currentRound % 2 === 1
          ? coaches
          : [...coaches].reverse();

    let nextRound = currentRound;
    let nextPickInRound = currentPickInRound + 1;
    let nextOrder = currentOrder;

    if (nextPickInRound > totalCoaches) {
      nextRound += 1;
      nextOrder = nextRound % 2 === 1 ? coaches : [...coaches].reverse();
      nextPickInRound = 1;
    }

    const nextCoach = nextOrder[nextPickInRound - 1] || null;
    const nextOverall = Number(turn.overall_pick || 0) + 1;

    return {
      ...currentDraftState,
      turn: {
        ...turn,
        current_round: nextRound,
        pick_in_round: nextPickInRound,
        overall_pick: nextOverall,
        current_coach: nextCoach,
        order_for_round: nextOrder,
      },
    };
  };

  const stopDraft = async () => {
    if (isPreview) return;
    if (!user?.isAdmin || !draftState?.active) return;
    if (
      !window.confirm(
        "Stop the current draft now? Coaches will no longer be able to pick.",
      )
    ) {
      return;
    }

    const token = getAccessToken();
    try {
      const res = await axios.post(
        `${API}/admin/draft/stop`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStatus(res.data?.message || "Draft stopped by admin.");
      await fetchDraftState();
    } catch (error) {
      setStatus(error?.response?.data?.detail || "Unable to stop draft.");
    }
  };

  const makePick = async (swimmerName) => {
    if (isPreview) return;
    if (!myTurn || isSubmittingPick) return;
    const token = getAccessToken();
    const previousAvailableSwimmers = availableSwimmers;
    const previousMyTeam = myTeam;
    const previousDraftState = draftState;
    const swimmer = availableSwimmers.find((item) => item.name === swimmerName);

    setStatus("");
    setToastMessage("Great pick!");
    setPendingPickName(swimmerName);
    setIsSubmittingPick(true);

    if (swimmer) {
      setAvailableSwimmers((prev) =>
        prev.filter((item) => item.name !== swimmerName),
      );
    }
    setMyTeam((prev) =>
      prev.includes(swimmerName) ? prev : [...prev, swimmerName],
    );
    setDraftState((prev) => computeNextDraftTurn(prev));

    try {
      await axios.post(
        `${API}/draft/pick`,
        { swimmer_name: swimmerName },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchDraftState();
    } catch (error) {
      setAvailableSwimmers(previousAvailableSwimmers);
      setMyTeam(previousMyTeam);
      setDraftState(previousDraftState);
      setToastMessage("");
      setStatus(error?.response?.data?.detail || "Unable to make draft pick.");
    } finally {
      setIsSubmittingPick(false);
      setPendingPickName(null);
    }
  };

  const openSwimmerDetails = (swimmer) => {
    if (!swimmer || !swimmer.name) return;
    setSelectedSwimmer(swimmer);
  };

  if (loading) {
    return <p>Loading draft board...</p>;
  }

  if (!draftState?.active && !draftState?.completed) {
    return (
      <div className="draft-page">
        <h1 className="draft-page-title">Season Draft</h1>
        <p>No active draft yet. Ask an admin to start the season draft.</p>
        {status && <p className="draft-error">{status}</p>}
      </div>
    );
  }

  return (
    <div className="draft-page">
      <h1 className="draft-page-title">Season Draft</h1>
      <div className="draft-status-bar">
        <span>
          <strong>{draftState?.is_mock ? "Mock Draft" : "Season Draft"}</strong>
        </span>
        <span>
          Round {draftState?.turn?.current_round || 0} · Pick{" "}
          {draftState?.turn?.overall_pick || 0}
        </span>
        <span>
          Current Turn:{" "}
          <strong>{draftState?.turn?.current_coach || "—"}</strong>
        </span>
        <span className={myTurn ? "draft-my-turn" : "draft-wait-turn"}>
          {myTurn ? "Your turn to draft" : "Waiting for your turn"}
        </span>
        <button
          className="draft-secondary-btn"
          onClick={fetchDraftState}
          type="button"
        >
          Refresh
        </button>
        {user?.isAdmin && draftState?.active ? (
          <button
            className="draft-secondary-btn"
            onClick={stopDraft}
            type="button"
          >
            Stop Draft
          </button>
        ) : null}
      </div>

      {toastMessage ? (
        <div className="draft-toast" role="status" aria-live="polite">
          {toastMessage}
        </div>
      ) : null}

      <p className="draft-small-text" style={{ marginTop: "0.15rem" }}>
        Round 1 order (randomized at start):{" "}
        {draftState?.coaches?.join(" → ") || "—"}
      </p>
      <p className="draft-small-text" style={{ marginTop: "0.15rem" }}>
        Auto-refreshing every {pollIntervalMs / 1000} seconds.
      </p>
      {draftState?.stopped_by_admin ? (
        <p className="draft-error">Draft was stopped by an admin.</p>
      ) : null}

      <div className="draft-layout">
        <section className="draft-main-panel draft-potential-panel">
          <h2 className="draft-section-title">Available Swimmers</h2>

          <div className="swimmer-summary-search-card draft-filter-panel">
            <div className="swimmer-summary-filter-group">
              <span className="swimmer-summary-filter-group-label">Search</span>
              <input
                type="text"
                className="swimmer-summary-search-input"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search swimmers by name"
                aria-label="Search swimmers by name"
              />
            </div>

            <div className="swimmer-summary-filter-group">
              <span className="swimmer-summary-filter-group-label">
                Age Group
              </span>
              <div className="swimmer-summary-filter-button-row">
                {AGE_GROUP_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={ageGroupFilter === option.value}
                    onClick={() => setAgeGroupFilter(option.value)}
                    className={
                      ageGroupFilter === option.value
                        ? "swimmer-summary-filter-btn swimmer-summary-filter-btn-active"
                        : "swimmer-summary-filter-btn"
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="swimmer-summary-filter-group">
              <span className="swimmer-summary-filter-group-label">Gender</span>
              <div className="swimmer-summary-filter-button-row">
                {GENDER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={genderFilter === option.value}
                    onClick={() => setGenderFilter(option.value)}
                    className={
                      genderFilter === option.value
                        ? "swimmer-summary-filter-btn swimmer-summary-filter-btn-active"
                        : "swimmer-summary-filter-btn"
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="draft-small-text draft-filter-count">
            Showing {filteredAvailableSwimmers.length} of{" "}
            {availableSwimmers.length} swimmers
          </p>

          <div className="table-scroll draft-table-scroll">
            <table className="draft-table stack-table">
              <thead>
                <tr>
                  <th className="draft-th-action">Action</th>
                  <th>Swimmer</th>
                  <th>Age</th>
                  <th>Points</th>
                  <th>Times</th>
                </tr>
              </thead>
              <tbody>
                {filteredAvailableSwimmers.map((swimmer) => (
                  <tr key={swimmer.name} className="stack-table-row">
                    <td className="draft-table-action-btn" data-label="Action">
                      <button
                        type="button"
                        className={
                          myTurn && !isSubmittingPick
                            ? "draft-pick-btn"
                            : "draft-pick-btn-disabled"
                        }
                        disabled={!myTurn || isSubmittingPick}
                        onClick={() => makePick(swimmer.name)}
                      >
                        {pendingPickName === swimmer.name && isSubmittingPick
                          ? "Picking..."
                          : "Draft"}
                      </button>
                    </td>
                    <td
                      className="draft-table-td-name draft-clickable-name-cell"
                      data-label="Swimmer"
                      role="button"
                      tabIndex={0}
                      onClick={() => openSwimmerDetails(swimmer)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openSwimmerDetails(swimmer);
                        }
                      }}
                    >
                      {swimmer.name}
                    </td>
                    <td className="draft-table td" data-label="Age">
                      {swimmer.age ?? "—"}
                    </td>
                    <td className="draft-table-td-points" data-label="Points">
                      {Number(swimmer.total_points || 0).toFixed(2)}
                    </td>
                    <td className="draft-table-td-times" data-label="Times">
                      {renderTimesSummary(swimmer.best_times || {})}
                    </td>
                  </tr>
                ))}
                {filteredAvailableSwimmers.length === 0 ? (
                  <tr className="stack-table-row">
                    <td
                      className="draft-table-td-name"
                      data-label="Swimmer"
                      colSpan={5}
                    >
                      No swimmers match the current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="draft-side-panel">
          <div className="draft-card draft-card-my-team">
            <h3 className="draft-card-title">My Team ({myTeam.length})</h3>
            {myTeam.length === 0 ? (
              <p className="draft-small-text">No picks yet.</p>
            ) : (
              <div className="draft-team-groups">
                {myTeamByAge.map((group) => (
                  <div
                    key={`age-${group.key}`}
                    className="draft-team-age-group"
                  >
                    <p className="draft-team-age-header">
                      {group.title} ({group.swimmers.length})
                    </p>
                    <ul className="draft-list draft-team-list">
                      {group.swimmers.map((swimmer) => (
                        <li
                          key={`team-${group.key}-${swimmer.name}`}
                          className={`draft-team-swimmer draft-team-swimmer-${swimmer.genderKey}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => openSwimmerDetails(swimmer.swimmer)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openSwimmerDetails(swimmer.swimmer);
                            }
                          }}
                        >
                          <span>{swimmer.name}</span>
                          <span className="draft-team-swimmer-gender">
                            {swimmer.genderLabel}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="draft-card draft-card-watched">
            <h3 className="draft-card-title">
              Watched Swimmers ({watchedSwimmers.length})
            </h3>
            {watchedSwimmers.length === 0 ? (
              <p className="draft-small-text">No watched swimmers saved.</p>
            ) : (
              <div className="draft-watch-list">
                {watchedSwimmers.map((swimmer) => (
                  <div
                    key={`watch-${swimmer.name}`}
                    className="draft-watch-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => openSwimmerDetails(swimmer)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openSwimmerDetails(swimmer);
                      }
                    }}
                  >
                    <strong>{swimmer.name}</strong>
                    <span className="draft-small-text">
                      Age: {swimmer.age ?? "—"}
                    </span>
                    <span className="draft-small-text">
                      Points: {Number(swimmer.total_points || 0).toFixed(2)}
                    </span>
                    <span className="draft-small-text">
                      {renderTimesSummary(swimmer.best_times || {})}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="draft-card draft-card-tips">
            <h3 className="draft-card-title">Tips for Drafting</h3>
            <ul className="draft-tip-list">
              {DRAFT_TIPS.map((tip, idx) => (
                <li key={`tip-${idx}`} className="draft-tip-item">
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {Array.isArray(draftState?.picks) && draftState.picks.length > 0 ? (
        <section className="draft-picks-log">
          <h2 className="draft-section-title">Pick Log</h2>
          <div className="table-scroll">
            <table className="draft-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Round</th>
                  <th>Coach</th>
                  <th>Swimmer</th>
                </tr>
              </thead>
              <tbody>
                {[...draftState.picks].reverse().map((pick) => (
                  <tr key={`pick-${pick.overall_pick}`}>
                    <td>{pick.overall_pick}</td>
                    <td>
                      {pick.round}-{pick.pick_in_round}
                    </td>
                    <td>{pick.coach}</td>
                    <td>{pick.swimmer_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {status && (
        <p
          className={
            status.startsWith("Drafted") ? "draft-success" : "draft-error"
          }
        >
          {status}
        </p>
      )}

      {selectedSwimmer ? (
        <SwimmerTracker
          swimmer={selectedSwimmer}
          onClose={() => setSelectedSwimmer(null)}
        />
      ) : null}
    </div>
  );
};

export default DraftBoard;
